/**
 * @fileoverview Reactivate User mutation hook.
 * @description Wraps POST /users/{userId}/reactivate in a TanStack mutation. The ETag is the one
 * of the details the dialog loaded and showed, so a user who changed since is refused (412 / 3022)
 * and shown for review. The hook owns the Idempotency-Key, so a retry after an
 * unknown outcome (lost response, timeout, 5xx) is a replay (not a second welcome-back email)
 * and a later command is a new one. On success it refreshes the directory, team,
 * summaries and filter options (the user comes back with a role and manager and changes the
 * counts), and leaves the open dialog's details alone.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useLifecycleMutation } from '@/features/users/hooks/useLifecycleMutation'
import { reactivateUser } from '@/features/users/services/users.service'

import type { ReactivatedUser, ReactivateUserRequest } from '@/features/users/types/users.types'

export interface ReactivateUserVariables {
  userId: string
  payload: ReactivateUserRequest
  /** The ETag of the details the dialog showed. */
  etag: string
}

/**
 * @description Reactivates a user. The Idempotency-Key, the cache refresh and the completion
 * callback come from useLifecycleMutation, so the callback still runs if the dialog unmounts
 * while the request is in flight.
 * @param {{ onSuccess?: (result: ReactivatedUser) => void }} [options] - What to do with the
 * result after a success.
 * @returns {import('@tanstack/react-query').UseMutationResult<ReactivatedUser, ApiError,
 * ReactivateUserVariables>} The mutation object (mutate, isPending, error, ...).
 */
export function useReactivateUser({
  onSuccess,
}: { onSuccess?: (result: ReactivatedUser) => void } = {}) {
  return useLifecycleMutation<ReactivatedUser, ReactivateUserVariables>({
    action: 'reactivate',
    /**
     * @description Sends the command with its idempotency key.
     * @param {ReactivateUserVariables} variables - The user, request and ETag.
     * @param {string} idempotencyKey - The key for this action on this user.
     * @returns {Promise<ReactivatedUser>} The command's result.
     */
    send: ({ userId, payload, etag }, idempotencyKey) =>
      reactivateUser(userId, payload, etag, idempotencyKey),
    onSuccess,
  })
}
